import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const email = params.get("email") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      toast.error("Invalid reset link");
    }
  }, [token, email]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/auth", { replace: true }), 1200);
    } catch (err) {
      toast.error(formatApiError(err, "Reset failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-7 shadow-xl">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h1 className="font-display text-2xl font-semibold tracking-tight">Set a new password</h1>
        </div>
        {done ? (
          <div className="mt-5 rounded-lg border border-emerald-500/30 bg-emerald-50/50 p-4 text-sm dark:bg-emerald-950/30">
            <div className="flex items-center gap-2 font-medium text-emerald-800 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> Password updated
            </div>
            <div className="mt-1 text-emerald-800/80 dark:text-emerald-300/80">Redirecting you to sign in…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <div className="text-sm text-muted-foreground">Account: <span className="font-medium text-foreground">{email || "—"}</span></div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-new">New password</Label>
              <Input id="rp-new" type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="reset-new-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-confirm">Confirm new password</Label>
              <Input id="rp-confirm" type="password" minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-confirm-password" />
            </div>
            <Button
              type="submit"
              disabled={busy || !token || !email}
              className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="reset-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
            </Button>
            <div className="pt-1 text-center text-[12.5px]">
              <Link to="/auth" className="font-semibold text-primary hover:underline">Back to sign in</Link>
            </div>
          </form>
        )}
      </Card>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
